import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Patient, Product, MedicalRecordAttachment } from '@/types';
import toast from 'react-hot-toast';
import { useMedicalRecords, useCreateMedicalRecord, useUpdateMedicalRecord, useAttachments, useUploadAttachment, useDeleteAttachment, useCreateVaccination, useUpdateVaccination, useDeleteVaccination, useCreateDeworming, useUpdateDeworming, useDeleteDeworming, useVaccinations, useDewormings } from '@/hooks/usePatients';
import { useProducts, useCategories } from '@/hooks/useProducts';
import { useCreateServiceOrder, useServiceOrders } from '@/hooks/useServiceOrders';
import { useGroomingAppointments } from '@/hooks/useGrooming';
import { SERVICE_TYPE_LABELS, STATUS_LABELS } from '@/services/grooming';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '@/services/serviceOrders';
import { patientsService } from '@/services/patients';
import { formatPrice } from '@/utils/format';
import { useAuthStore } from '@/context/authStore';
import { generateMedicalHistory } from '@/services/pdfService';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import clsx from 'clsx';
import {
  Dog,
  Scale,
  User,
  Phone,
  Plus,
  PlusCircle,
  Thermometer,
  Stethoscope,
  Pill,
  ChevronDown,
  ChevronUp,
  Paperclip,
  Upload,
  X,
  Download,
  Image,
  File,
  Trash2,
  Search,
  ShoppingCart,
  DollarSign,
  Check,
  Package,
  Syringe,
  Bug,
  Scissors,
  ExternalLink,
  FileText,
  Grid,
  Wrench,
  Edit,
  Mail,
  PawPrint,
} from 'lucide-react';

/**
 * ISO -> "AAAA-MM-DD" para un <input type="date">, en hora LOCAL.
 * Con toISOString() se corre un dia para atras (Argentina esta en UTC-3).
 */
const toDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** "AAAA-MM-DD" -> ISO al mediodia: asi la fecha no se corre al pasar a UTC. */
const fromDateInput = (fecha: string): string | undefined =>
  fecha ? `${fecha}T12:00:00` : undefined;

// Interface para items de la comanda
interface OrderItemDraft {
  id: string; // ID único para el item (puede ser product_id o generado)
  product_id: number | null; // null para items personalizados
  product_name: string;
  quantity: number;
  unit_price: number;
  reference_price: number;
  should_charge: boolean;
  notes: string;
  is_custom: boolean; // true si es item personalizado
  // Para productos sell_by_ml
  is_ml_product: boolean;
  ml_quantity?: number;
  price_per_ml?: number;
}

interface PatientDetailModalProps {
  patient: Patient | null;
  isOpen: boolean;
  onClose: () => void;
  // Con qué pestaña abre: desde el nombre de la mascota se entra por los datos,
  // desde el icono de historia clínica se entra por la historia.
  initialTab?: 'info' | 'history';
}

export default function PatientDetailModal({
  patient,
  isOpen,
  onClose,
  initialTab = 'history',
}: PatientDetailModalProps) {
  const navigate = useNavigate();
  const { tenant } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'info' | 'history' | 'services' | 'new-record'>(initialTab);
  const [expandedRecord, setExpandedRecord] = useState<number | null>(null);

  // Al abrir otra mascota (o entrar por otro lado) volver a la pestaña que corresponde
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab, patient?.id]);
  const [uploadingToRecord, setUploadingToRecord] = useState<number | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadType, setUploadType] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estado para búsqueda de productos
  const [productSearch, setProductSearch] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);

  // Estado para items de la comanda
  const [orderItems, setOrderItems] = useState<OrderItemDraft[]>([]);

  // Estado para item personalizado
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customItem, setCustomItem] = useState({ name: '', price: '' });

  // Estado para modal de catálogo de productos
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategory, setCatalogCategory] = useState('');

  // Estado para modal de productos sell_by_ml
  const [mlProductPending, setMlProductPending] = useState<Product | null>(null);
  const [mlQuantityInput, setMlQuantityInput] = useState('');

  const { data: medicalRecords, isLoading: loadingRecords } = useMedicalRecords(
    isOpen && patient?.id ? patient.id : null
  );
  const createRecord = useCreateMedicalRecord();
  const updateRecord = useUpdateMedicalRecord();

  // Estado para editar registro médico
  const [editingRecord, setEditingRecord] = useState<{
    id: number;
    reason: string;
    symptoms: string;
    diagnosis: string;
    treatment: string;
    prescription: string;
    weight: string;
    temperature: string;
    next_visit_date: string;
    next_visit_reason: string;
    notes: string;
  } | null>(null);

  // Historial de vacunas y desparasitaciones
  const { data: vaccinationHistory, isLoading: loadingVaccinations } = useVaccinations(
    isOpen && patient?.id ? patient.id : null
  );
  const { data: dewormingHistory, isLoading: loadingDewormings } = useDewormings(
    isOpen && patient?.id ? patient.id : null
  );

  // Datos para pestaña de servicios
  const { data: groomingAppointments, isLoading: loadingGrooming } = useGroomingAppointments(
    isOpen && patient?.id ? { patient_id: patient.id, status: 'completed' as const } : undefined
  );
  const { data: patientServiceOrders, isLoading: loadingOrders } = useServiceOrders(
    isOpen && patient?.id ? { patient_id: patient.id } : undefined
  );
  const { data: currentAttachments } = useAttachments(expandedRecord);
  const uploadAttachment = useUploadAttachment();
  const deleteAttachment = useDeleteAttachment();
  const createVaccination = useCreateVaccination();
  const createDeworming = useCreateDeworming();
  const updateVaccination = useUpdateVaccination();
  const updateDeworming = useUpdateDeworming();
  const deleteVaccination = useDeleteVaccination();
  const deleteDeworming = useDeleteDeworming();

  // Edicion de un registro ya cargado del historial. Hace falta porque la fecha de
  // aplicacion se guarda sola al dar el alta: una vacuna aplicada antes (o un carnet
  // que se pasa a sistema) quedaba con la fecha del dia en que se tipeo.
  const [editingVaccinationId, setEditingVaccinationId] = useState<number | null>(null);
  const [editVaccinationData, setEditVaccinationData] = useState({
    application_date: '',
    next_dose_date: '',
    batch_number: '',
    dose_number: '',
    dose_ml: '',
    notes: '',
  });
  const [editingDewormingId, setEditingDewormingId] = useState<number | null>(null);
  const [editDewormingData, setEditDewormingData] = useState({
    application_date: '',
    next_dose_date: '',
    deworming_type: 'internal',
    batch_number: '',
    dose: '',
    notes: '',
  });

  const startEditVaccination = (vac: any) => {
    setEditingDewormingId(null);
    setEditingVaccinationId(vac.id);
    setEditVaccinationData({
      application_date: toDateInput(vac.application_date),
      next_dose_date: toDateInput(vac.next_dose_date),
      batch_number: vac.batch_number || '',
      dose_number: vac.dose_number?.toString() || '',
      dose_ml: vac.dose_ml?.toString() || '',
      notes: vac.notes || '',
    });
  };

  const handleSaveVaccination = async () => {
    if (!editingVaccinationId || !patient) return;
    try {
      await updateVaccination.mutateAsync({
        patientId: patient.id,
        recordId: editingVaccinationId,
        data: {
          application_date: fromDateInput(editVaccinationData.application_date),
          // null vacia la proxima dosis (y con eso deja de avisar)
          next_dose_date: editVaccinationData.next_dose_date || null,
          batch_number: editVaccinationData.batch_number,
          dose_number: editVaccinationData.dose_number
            ? Number(editVaccinationData.dose_number)
            : undefined,
          dose_ml: editVaccinationData.dose_ml ? Number(editVaccinationData.dose_ml) : undefined,
          notes: editVaccinationData.notes,
        },
      });
      setEditingVaccinationId(null);
    } catch (error) {
      console.error('Error al guardar la vacuna:', error);
      toast.error('No se pudo guardar el cambio. Intentá de nuevo.');
    }
  };

  // Borrar es para deshacer una carga equivocada: se pide desde el panel de
  // edicion (dos pasos) y con confirmacion, porque no tiene vuelta atras.
  const handleDeleteVaccination = async (vac: any) => {
    if (!patient) return;
    const cual = vac.product_name || 'esta vacuna';
    if (!confirm(`¿Borrar ${cual} del historial? No se puede deshacer.`)) return;
    try {
      await deleteVaccination.mutateAsync({ patientId: patient.id, recordId: vac.id });
      setEditingVaccinationId(null);
    } catch (error) {
      console.error('Error al borrar la vacuna:', error);
      toast.error('No se pudo borrar. Intentá de nuevo.');
    }
  };

  const startEditDeworming = (dew: any) => {
    setEditingVaccinationId(null);
    setEditingDewormingId(dew.id);
    setEditDewormingData({
      application_date: toDateInput(dew.application_date),
      next_dose_date: toDateInput(dew.next_dose_date),
      deworming_type: dew.deworming_type || 'internal',
      batch_number: dew.batch_number || '',
      dose: dew.dose || '',
      notes: dew.notes || '',
    });
  };

  const handleSaveDeworming = async () => {
    if (!editingDewormingId || !patient) return;
    try {
      await updateDeworming.mutateAsync({
        patientId: patient.id,
        recordId: editingDewormingId,
        data: {
          application_date: fromDateInput(editDewormingData.application_date),
          next_dose_date: editDewormingData.next_dose_date || null,
          deworming_type: editDewormingData.deworming_type as any,
          batch_number: editDewormingData.batch_number,
          dose: editDewormingData.dose,
          notes: editDewormingData.notes,
        },
      });
      setEditingDewormingId(null);
    } catch (error) {
      console.error('Error al guardar la desparasitación:', error);
      toast.error('No se pudo guardar el cambio. Intentá de nuevo.');
    }
  };

  // Estado para secciones colapsables de vacunación y desparasitación
  const [showVaccinationSection, setShowVaccinationSection] = useState(false);
  const [showDewormingSection, setShowDewormingSection] = useState(false);
  const [showOrderSection, setShowOrderSection] = useState(false);

  // Estado para vacunación
  const [vaccinationData, setVaccinationData] = useState({
    product_id: '',
    batch_number: '',
    dose_number: '1',
    dose_ml: '',
    next_dose_date: '',
    next_dose_notes: '',
    notes: '',
  });
  const [vaccinations, setVaccinations] = useState<Array<typeof vaccinationData & { product_name: string }>>([]);

  // Estado para desparasitación
  const [dewormingData, setDewormingData] = useState({
    product_id: '',
    product_name: '',
    deworming_type: 'internal' as 'internal' | 'external',
    batch_number: '',
    dose: '',
    next_dose_date: '',
    next_dose_notes: '',
    notes: '',
  });
  const [dewormings, setDewormings] = useState<Array<typeof dewormingData>>([]);

  // Buscador de vacunas (declarado antes de usarse en useProducts)
  const [vaccineSearch, setVaccineSearch] = useState('');
  const [showVaccineSearch, setShowVaccineSearch] = useState(false);

  // Búsqueda de productos (backend ordena por relevancia)
  const { data: searchedProducts = [] } = useProducts(
    { only_active: true, search: productSearch, limit: 200 },
    { enabled: isOpen && !!productSearch && productSearch.length >= 2 }
  );

  // Búsqueda de vacunas
  const { data: searchedVaccines = [] } = useProducts(
    { only_active: true, search: vaccineSearch, limit: 200 },
    { enabled: isOpen && !!vaccineSearch && vaccineSearch.length >= 2 }
  );

  // Todos los productos para el modal de catálogo
  const { data: allProducts = [], isLoading: loadingAllProducts } = useProducts(
    { only_active: true },
    { enabled: isOpen && showCatalogModal }
  );
  const { data: categories = [] } = useCategories();

  const createServiceOrder = useCreateServiceOrder();

  // Filtrar vacunas de los resultados
  const filteredVaccines = useMemo(() => {
    if (!vaccineSearch.trim() || vaccineSearch.length < 2) return [];
    return searchedVaccines
      .filter(p => {
        const categoryName = typeof p.category === 'string' ? p.category : p.category?.name || '';
        return categoryName.toLowerCase().includes('vacuna') || p.name.toLowerCase().includes('vacuna');
      })
      .slice(0, 10);
  }, [searchedVaccines, vaccineSearch]);

  // Productos filtrados (backend ya ordena por relevancia)
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim() || productSearch.length < 2) return [];
    return searchedProducts.slice(0, 50);
  }, [searchedProducts, productSearch]);

  // Productos filtrados para el catálogo
  const catalogProducts = useMemo(() => {
    let filtered = allProducts;

    // Filtrar por categoría
    if (catalogCategory) {
      filtered = filtered.filter(p => p.category_id === parseInt(catalogCategory));
    }

    // Filtrar por búsqueda
    if (catalogSearch.trim()) {
      const search = catalogSearch.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(search) ||
        p.code?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [allProducts, catalogCategory, catalogSearch]);

  const [newRecord, setNewRecord] = useState({
    reason: '',
    symptoms: '',
    diagnosis: '',
    treatment: '',
    prescription: '',
    weight: '',
    temperature: '',
    next_visit_date: '',
    next_visit_reason: '',
    notes: '',
  });

  const getSexLabel = (sex: string) => {
    switch (sex) {
      case 'male':
        return 'Macho';
      case 'female':
        return 'Hembra';
      default:
        return 'No especificado';
    }
  };

  const getSizeLabel = (size?: string) => {
    switch (size) {
      case 'xs':
        return '0-10 kg';
      case 'small':
        return '10-20 kg';
      case 'medium':
        return '20-30 kg';
      case 'large':
        return '30-40 kg';
      case 'xl':
        return '+40 kg';
      default:
        return null;
    }
  };

  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return 'No especificada';
    const birth = new Date(birthDate);
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      years--;
    }
    if (years < 1) {
      const months =
        (today.getFullYear() - birth.getFullYear()) * 12 +
        today.getMonth() -
        birth.getMonth();
      return `${months} meses`;
    }
    return `${years} años`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const attachmentTypeOptions = [
    { value: '', label: 'Tipo de archivo' },
    { value: 'analysis', label: 'Análisis' },
    { value: 'xray', label: 'Radiografía' },
    { value: 'ultrasound', label: 'Ecografía' },
    { value: 'photo', label: 'Foto' },
    { value: 'other', label: 'Otro' },
  ];

  const getAttachmentTypeLabel = (type?: string) => {
    const option = attachmentTypeOptions.find(o => o.value === type);
    return option?.label || 'Archivo';
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (fileType?: string) => {
    if (fileType?.startsWith('image/')) return Image;
    return File;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingToRecord) return;

    try {
      await uploadAttachment.mutateAsync({
        recordId: uploadingToRecord,
        file,
        description: uploadDescription || undefined,
        attachmentType: uploadType || undefined,
      });
      setUploadingToRecord(null);
      setUploadDescription('');
      setUploadType('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleDownloadAttachment = async (attachment: MedicalRecordAttachment) => {
    try {
      await patientsService.downloadAttachment(attachment.id, attachment.file_name);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('No se pudo descargar el archivo');
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!confirm('¿Eliminar este archivo?')) return;
    try {
      await deleteAttachment.mutateAsync(attachmentId);
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  // Abrir editor de registro médico
  const handleEditRecord = (record: any) => {
    setEditingRecord({
      id: record.id,
      reason: record.reason || '',
      symptoms: record.symptoms || '',
      diagnosis: record.diagnosis || '',
      treatment: record.treatment || '',
      prescription: record.prescription || '',
      weight: record.weight?.toString() || '',
      temperature: record.temperature?.toString() || '',
      next_visit_date: record.next_visit_date || '',
      next_visit_reason: record.next_visit_reason || '',
      notes: record.notes || '',
    });
  };

  // Guardar cambios en registro médico
  const handleSaveRecord = async () => {
    if (!editingRecord || !patient) return;

    try {
      // 1. Actualizar el registro médico
      await updateRecord.mutateAsync({
        recordId: editingRecord.id,
        data: {
          reason: editingRecord.reason || undefined,
          symptoms: editingRecord.symptoms || undefined,
          diagnosis: editingRecord.diagnosis || undefined,
          treatment: editingRecord.treatment || undefined,
          prescription: editingRecord.prescription || undefined,
          weight: editingRecord.weight ? parseFloat(editingRecord.weight) : undefined,
          temperature: editingRecord.temperature ? parseFloat(editingRecord.temperature) : undefined,
          next_visit_date: editingRecord.next_visit_date || undefined,
          next_visit_reason: editingRecord.next_visit_reason || undefined,
          notes: editingRecord.notes || undefined,
        },
      });

      // 2. Crear todas las vacunaciones de la lista (incluir datos pendientes del formulario)
      const allVaccinations = [...vaccinations];
      if (vaccinationData.product_id && vaccineSearch.trim()) {
        allVaccinations.push({ ...vaccinationData, product_name: vaccineSearch });
      }
      if (allVaccinations.length > 0) {
        for (const vac of allVaccinations) {
          await createVaccination.mutateAsync({
            patientId: patient.id,
            data: {
              patient_id: patient.id,
              product_id: parseInt(vac.product_id),
              medical_record_id: editingRecord.id,
              batch_number: vac.batch_number || undefined,
              dose_number: parseInt(vac.dose_number) || 1,
              dose_ml: vac.dose_ml ? parseFloat(vac.dose_ml) : undefined,
              next_dose_date: vac.next_dose_date || undefined,
              next_dose_notes: vac.next_dose_notes || undefined,
              notes: vac.notes || undefined,
            },
          });
        }
      }

      // 3. Si hay desparasitaciones, crear los registros (incluir datos pendientes del formulario)
      const allDewormings = [...dewormings];
      if (dewormingData.product_name?.trim()) {
        allDewormings.push({ ...dewormingData });
      }
      for (const dew of allDewormings) {
        await createDeworming.mutateAsync({
          patientId: patient.id,
          data: {
            patient_id: patient.id,
            product_id: dew.product_id ? parseInt(dew.product_id) : undefined,
            product_name: dew.product_name || undefined,
            medical_record_id: editingRecord.id,
            deworming_type: dew.deworming_type,
            batch_number: dew.batch_number || undefined,
            dose: dew.dose || undefined,
            next_dose_date: dew.next_dose_date || undefined,
            next_dose_notes: dew.next_dose_notes || undefined,
            notes: dew.notes || undefined,
          },
        });
      }

      // 4. Si hay items para cobrar, crear la comanda
      const itemsToCharge = orderItems.filter(item => item.should_charge);
      if (itemsToCharge.length > 0) {
        await createServiceOrder.mutateAsync({
          patient_id: patient.id,
          owner_id: patient.owner_id,
          medical_record_id: editingRecord.id,
          notes: `Consulta: ${editingRecord.reason || 'Consulta general'}`,
          items: itemsToCharge.map(item => ({
            product_id: item.product_id || undefined,
            custom_name: item.is_custom ? item.product_name : undefined,
            quantity: item.quantity,
            unit_price: item.unit_price,
            ml_quantity: item.is_ml_product ? item.ml_quantity : undefined,
            notes: item.notes || undefined,
          })),
        });
      }

      // 5. Limpiar todo
      setEditingRecord(null);
      setVaccinations([]);
      setVaccinationData({
        product_id: '',
        batch_number: '',
        dose_number: '1',
        dose_ml: '',
        next_dose_date: '',
        next_dose_notes: '',
        notes: '',
      });
      setDewormings([]);
      setDewormingData({
        product_id: '',
        product_name: '',
        deworming_type: 'internal',
        batch_number: '',
        dose: '',
        next_dose_date: '',
        next_dose_notes: '',
        notes: '',
      });
      setOrderItems([]);
      setVaccineSearch('');
      setProductSearch('');
    } catch (error) {
      console.error('Error updating record:', error);
    }
  };

  // Agregar producto a la comanda
  const handleAddProduct = (product: Product) => {
    // Si es producto sell_by_ml, mostrar modal para ingresar cantidad de ML
    if (product.sell_by_ml && product.price_per_ml) {
      setMlProductPending(product);
      setMlQuantityInput('');
      setProductSearch('');
      setShowProductSearch(false);
      return;
    }

    // Verificar si ya existe
    const exists = orderItems.find(item => item.product_id === product.id && !item.is_custom && !item.is_ml_product);
    if (exists) {
      // Incrementar cantidad
      setOrderItems(items =>
        items.map(item =>
          item.product_id === product.id && !item.is_custom && !item.is_ml_product
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      // Agregar nuevo
      setOrderItems([
        ...orderItems,
        {
          id: `product-${product.id}`,
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: Number(product.sale_price) || 0,
          reference_price: Number(product.sale_price) || 0,
          should_charge: true,
          notes: '',
          is_custom: false,
          is_ml_product: false,
        },
      ]);
    }
    setProductSearch('');
    setShowProductSearch(false);
  };

  // Confirmar agregar producto sell_by_ml
  const handleConfirmMlProduct = () => {
    if (!mlProductPending || !mlQuantityInput) return;
    const ml = parseFloat(mlQuantityInput);
    if (isNaN(ml) || ml <= 0) return;

    const pricePerMl = Number(mlProductPending.price_per_ml) || 0;
    const calculatedPrice = ml * pricePerMl;

    setOrderItems([
      ...orderItems,
      {
        id: `ml-${mlProductPending.id}-${Date.now()}`,
        product_id: mlProductPending.id,
        product_name: mlProductPending.name,
        quantity: 1,
        unit_price: calculatedPrice,
        reference_price: calculatedPrice,
        should_charge: true,
        notes: '',
        is_custom: false,
        is_ml_product: true,
        ml_quantity: ml,
        price_per_ml: pricePerMl,
      },
    ]);

    setMlProductPending(null);
    setMlQuantityInput('');
  };

  // Cancelar agregar producto sell_by_ml
  const handleCancelMlProduct = () => {
    setMlProductPending(null);
    setMlQuantityInput('');
  };

  // Agregar item personalizado
  const handleAddCustomItem = () => {
    if (!customItem.name.trim()) return;
    const price = parseFloat(customItem.price) || 0;
    setOrderItems([
      ...orderItems,
      {
        id: `custom-${Date.now()}`,
        product_id: null,
        product_name: customItem.name.trim(),
        quantity: 1,
        unit_price: price,
        reference_price: price,
        should_charge: true,
        notes: '',
        is_custom: true,
        is_ml_product: false,
      },
    ]);
    setCustomItem({ name: '', price: '' });
    setShowCustomForm(false);
  };

  // Actualizar item de la comanda
  const handleUpdateItem = (itemId: string, field: keyof OrderItemDraft, value: any) => {
    setOrderItems(items =>
      items.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  // Agregar vacuna a la lista
  const handleAddVaccine = () => {
    if (!vaccinationData.product_id || !vaccineSearch.trim()) {
      alert('Selecciona una vacuna');
      return;
    }

    setVaccinations([
      ...vaccinations,
      {
        ...vaccinationData,
        product_name: vaccineSearch,
      }
    ]);

    // Limpiar formulario
    setVaccinationData({
      product_id: '',
      batch_number: '',
      dose_number: '1',
      dose_ml: '',
      next_dose_date: '',
      next_dose_notes: '',
      notes: '',
    });
    setVaccineSearch('');
  };

  // Eliminar vacuna de la lista
  const handleRemoveVaccine = (index: number) => {
    setVaccinations(vaccinations.filter((_, i) => i !== index));
  };

  // Agregar desparasitación a la lista
  const handleAddDeworming = () => {
    if (!dewormingData.product_name.trim()) {
      alert('Ingresa el producto/medicamento');
      return;
    }

    setDewormings([...dewormings, { ...dewormingData }]);

    // Limpiar formulario
    setDewormingData({
      product_id: '',
      product_name: '',
      deworming_type: 'internal',
      batch_number: '',
      dose: '',
      next_dose_date: '',
      next_dose_notes: '',
      notes: '',
    });
  };

  // Eliminar desparasitación de la lista
  const handleRemoveDeworming = (index: number) => {
    setDewormings(dewormings.filter((_, i) => i !== index));
  };

  // Eliminar item de la comanda
  const handleRemoveItem = (itemId: string) => {
    setOrderItems(items => items.filter(item => item.id !== itemId));
  };

  // Calcular total de items a cobrar
  const orderTotal = useMemo(() => {
    return orderItems
      .filter(item => item.should_charge)
      .reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  }, [orderItems]);

  const handleCreateRecord = async () => {
    if (!patient) return;
    try {
      // 1. Crear el registro médico
      const createdRecord = await createRecord.mutateAsync({
        patientId: patient.id,
        data: {
          patient_id: patient.id,
          reason: newRecord.reason || undefined,
          symptoms: newRecord.symptoms || undefined,
          diagnosis: newRecord.diagnosis || undefined,
          treatment: newRecord.treatment || undefined,
          prescription: newRecord.prescription || undefined,
          weight: newRecord.weight ? Number(newRecord.weight) : undefined,
          temperature: newRecord.temperature ? Number(newRecord.temperature) : undefined,
          next_visit_date: newRecord.next_visit_date || undefined,
          next_visit_reason: newRecord.next_visit_reason || undefined,
          notes: newRecord.notes || undefined,
        },
      });

      // 2. Crear todas las vacunaciones de la lista (incluir datos pendientes del formulario)
      const allVaccinations = [...vaccinations];
      if (vaccinationData.product_id && vaccineSearch.trim()) {
        allVaccinations.push({ ...vaccinationData, product_name: vaccineSearch });
      }
      if (allVaccinations.length > 0 && createdRecord) {
        for (const vac of allVaccinations) {
          await createVaccination.mutateAsync({
            patientId: patient.id,
            data: {
              patient_id: patient.id,
              product_id: parseInt(vac.product_id),
              medical_record_id: createdRecord.id,
              batch_number: vac.batch_number || undefined,
              dose_number: parseInt(vac.dose_number) || 1,
              dose_ml: vac.dose_ml ? parseFloat(vac.dose_ml) : undefined,
              next_dose_date: vac.next_dose_date || undefined,
              next_dose_notes: vac.next_dose_notes || undefined,
              notes: vac.notes || undefined,
            },
          });
        }
      }

      // 3. Si hay desparasitaciones, crear los registros (incluir datos pendientes del formulario)
      const allDewormingsCreate = [...dewormings];
      if (dewormingData.product_name?.trim()) {
        allDewormingsCreate.push({ ...dewormingData });
      }
      if (createdRecord) {
        for (const dew of allDewormingsCreate) {
          await createDeworming.mutateAsync({
            patientId: patient.id,
            data: {
              patient_id: patient.id,
              product_id: dew.product_id ? parseInt(dew.product_id) : undefined,
              product_name: dew.product_name || undefined,
              medical_record_id: createdRecord.id,
              deworming_type: dew.deworming_type,
              batch_number: dew.batch_number || undefined,
              dose: dew.dose || undefined,
              next_dose_date: dew.next_dose_date || undefined,
              next_dose_notes: dew.next_dose_notes || undefined,
              notes: dew.notes || undefined,
            },
          });
        }
      }

      // 4. Si hay items para cobrar, crear la comanda
      const itemsToCharge = orderItems.filter(item => item.should_charge);
      if (itemsToCharge.length > 0 && createdRecord) {
        await createServiceOrder.mutateAsync({
          patient_id: patient.id,
          owner_id: patient.owner_id,
          medical_record_id: createdRecord.id,
          notes: `Consulta: ${newRecord.reason || 'Consulta general'}`,
          items: itemsToCharge.map(item => ({
            product_id: item.product_id || undefined,
            custom_name: item.is_custom ? item.product_name : undefined,
            quantity: item.quantity,
            unit_price: item.unit_price,
            ml_quantity: item.is_ml_product ? item.ml_quantity : undefined,
            notes: item.notes || undefined,
          })),
        });
      }

      // 5. Limpiar formulario
      setNewRecord({
        reason: '',
        symptoms: '',
        diagnosis: '',
        treatment: '',
        prescription: '',
        weight: '',
        temperature: '',
        next_visit_date: '',
        next_visit_reason: '',
        notes: '',
      });
      setOrderItems([]);
      setVaccinations([]);
      setVaccinationData({
        product_id: '',
        batch_number: '',
        dose_number: '1',
        dose_ml: '',
        next_dose_date: '',
        next_dose_notes: '',
        notes: '',
      });
      setDewormings([]);
      setDewormingData({
        product_id: '',
        product_name: '',
        deworming_type: 'internal',
        batch_number: '',
        dose: '',
        next_dose_date: '',
        next_dose_notes: '',
        notes: '',
      });
      setVaccineSearch('');
      setShowVaccinationSection(false);
      setShowDewormingSection(false);
      setActiveTab('history');
    } catch (error) {
      console.error('Error creating record:', error);
    }
  };

  // Función para descargar la historia clínica completa en PDF
  const handleDownloadMedicalHistory = () => {
    if (!patient || !tenant) return;

    generateMedicalHistory(
      {
        patient,
        medicalRecords: medicalRecords || [],
        vaccinations: vaccinationHistory || [],
        dewormings: dewormingHistory || [],
      },
      tenant
    );
  };

  if (!patient) return null;

  const handleDeleteDeworming = async (dew: any) => {
    if (!patient) return;
    const cual = dew.product_name || 'esta desparasitación';
    if (!confirm(`¿Borrar ${cual} del historial? No se puede deshacer.`)) return;
    try {
      await deleteDeworming.mutateAsync({ patientId: patient.id, recordId: dew.id });
      setEditingDewormingId(null);
    } catch (error) {
      console.error('Error al borrar la desparasitación:', error);
      toast.error('No se pudo borrar. Intentá de nuevo.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      {/* En mobile el modal entero scrollea como una sola columna; en desktop
          (lg) cada panel maneja su propio scroll (overflow-hidden + paneles). */}
      <div className="flex flex-col lg:flex-row h-full min-h-0 overflow-y-auto lg:overflow-hidden">
        {/* Panel Izquierdo - Información del Paciente */}
        <div className="lg:w-80 flex-shrink-0 bg-gradient-to-b from-taupe-50 to-cream-50 border-b lg:border-b-0 lg:border-r border-gray-200 p-6 lg:overflow-y-auto">
          {/* Cabecera del paciente */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-taupe-400 to-cream-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
              <Dog className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              {patient.name}
              {patient.deceased && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 font-normal">
                  Falleció{patient.deceased_date ? ` · ${new Date(patient.deceased_date + 'T00:00:00').toLocaleDateString('es-AR')}` : ''}
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500">{calculateAge(patient.birth_date)}</p>
            {patient.birth_date && (
              <p className="text-xs text-gray-400">
                {new Date(patient.birth_date).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </p>
            )}
          </div>

          {/* Datos del paciente */}
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <span className="text-gray-400 w-20">Especie:</span>
              <span className="font-medium">{patient.species}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <span className="text-gray-400 w-20">Raza:</span>
              <span className="font-medium">{patient.breed || '-'}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <span className="text-gray-400 w-20">Sexo:</span>
              <span className="font-medium">{getSexLabel(patient.sex)}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <span className="text-gray-400 w-20">Castrado:</span>
              <span className={clsx(
                "font-medium",
                patient.is_neutered === true && "text-green-600",
                patient.is_neutered === false && "text-orange-600"
              )}>
                {patient.is_neutered === true ? 'Sí' : patient.is_neutered === false ? 'No' : '-'}
              </span>
            </div>
            {patient.color && (
              <div className="flex items-center gap-2 text-gray-600">
                <span className="text-gray-400 w-20">Color:</span>
                <span className="font-medium">{patient.color}</span>
              </div>
            )}
            {getSizeLabel(patient.size) && (
              <div className="flex items-center gap-2 text-gray-600">
                <span className="text-gray-400 w-20">Tamaño:</span>
                <span className="font-medium">{getSizeLabel(patient.size)}</span>
              </div>
            )}
            {patient.weight && (
              <div className="flex items-center gap-2 text-gray-600">
                <span className="text-gray-400 w-20">Peso:</span>
                <span className="font-medium">{patient.weight} kg</span>
              </div>
            )}
            {patient.microchip_number && (
              <div className="flex items-center gap-2 text-gray-600">
                <span className="text-gray-400 w-20">Microchip:</span>
                <span className="font-medium text-xs">{patient.microchip_number}</span>
              </div>
            )}
          </div>

          {/* Alergias */}
          {patient.allergies && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs font-semibold text-red-700 uppercase mb-1">Alergias</p>
              <p className="text-sm text-red-800">{patient.allergies}</p>
            </div>
          )}

          {/* Notas */}
          {patient.notes && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs font-semibold text-amber-700 uppercase mb-1">Notas</p>
              <p className="text-sm text-amber-800">{patient.notes}</p>
            </div>
          )}

          {/* Propietario */}
          {patient.owner && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Propietario</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-taupe-200 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-taupe-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{patient.owner.first_name} {patient.owner.last_name}</p>
                  {patient.owner.phone && (
                    <a href={`tel:${patient.owner.phone}`} className="text-sm text-taupe-600 hover:text-taupe-800 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {patient.owner.phone}
                    </a>
                  )}
                  {patient.owner.email && (
                    <a href={`mailto:${patient.owner.email}`} className="text-sm text-taupe-600 hover:text-taupe-800 flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {patient.owner.email}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Botón descargar historia clínica */}
          <div className="mt-6">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownloadMedicalHistory}
              disabled={!tenant}
              className="w-full"
              title="Descargar historia clínica completa en PDF"
            >
              <FileText className="h-4 w-4 mr-2" />
              Descargar Historia Clínica
            </Button>
          </div>
        </div>

        {/* Panel Derecho - Historia Clínica */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-white overflow-visible lg:overflow-hidden">
          {/* Tabs con botón cerrar */}
          <div className="flex items-center border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex flex-1 px-6">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'info'
                    ? 'border-taupe-600 text-taupe-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <PawPrint className="h-4 w-4 inline mr-1.5" />
                Datos
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-taupe-600 text-taupe-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Stethoscope className="h-4 w-4 inline mr-1.5" />
                Historia Clínica
              </button>
              <button
                onClick={() => setActiveTab('services')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'services'
                    ? 'border-taupe-600 text-taupe-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <ShoppingCart className="h-4 w-4 inline mr-1.5" />
                Servicios
              </button>
              <button
                onClick={() => setActiveTab('new-record')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'new-record'
                    ? 'border-taupe-600 text-taupe-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Plus className="h-4 w-4 inline mr-1.5" />
                Nueva Consulta
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-3 mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Cerrar"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Contenido scrolleable (en desktop; en mobile scrollea el modal entero) */}
          <div className="flex-1 lg:min-h-0 lg:overflow-y-auto p-6">
        {activeTab === 'info' && (
          <div className="space-y-4">
            {patient.deceased && (
              <div className="p-3 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700">
                Esta mascota está marcada como fallecida
                {patient.deceased_date
                  ? ` el ${new Date(patient.deceased_date + 'T00:00:00').toLocaleDateString('es-AR')}`
                  : ''}
                , así que no recibe recordatorios de vacunas ni de controles.
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Datos de la mascota</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: 'Nombre', value: patient.name },
                  { label: 'Especie', value: patient.species },
                  { label: 'Raza', value: patient.breed || '-' },
                  { label: 'Sexo', value: getSexLabel(patient.sex) },
                  { label: 'Edad', value: calculateAge(patient.birth_date) },
                  {
                    label: 'Nacimiento',
                    value: patient.birth_date
                      ? new Date(patient.birth_date).toLocaleDateString('es-AR')
                      : '-',
                  },
                  {
                    label: 'Castrado',
                    value: patient.is_neutered === true ? 'Sí' : patient.is_neutered === false ? 'No' : '-',
                  },
                  { label: 'Color', value: patient.color || '-' },
                  { label: 'Tamaño', value: getSizeLabel(patient.size) || '-' },
                  { label: 'Peso', value: patient.weight ? `${patient.weight} kg` : '-' },
                  { label: 'Microchip', value: patient.microchip_number || '-' },
                ].map((f) => (
                  <div key={f.label} className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-gray-400 uppercase">{f.label}</p>
                    <p className="text-sm font-medium text-gray-800 break-words">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {patient.allergies && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs font-semibold text-red-700 uppercase mb-1">Alergias</p>
                <p className="text-sm text-red-800 whitespace-pre-wrap">{patient.allergies}</p>
              </div>
            )}

            {patient.notes && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-semibold text-amber-700 uppercase mb-1">Notas</p>
                <p className="text-sm text-amber-800 whitespace-pre-wrap">{patient.notes}</p>
              </div>
            )}

            {patient.owner && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Propietario</p>
                <div className="border border-gray-200 rounded-lg p-3 space-y-1">
                  <p className="font-medium text-gray-900">
                    {patient.owner.first_name} {patient.owner.last_name}
                  </p>
                  {patient.owner.phone && (
                    <a href={`tel:${patient.owner.phone}`} className="text-sm text-taupe-600 hover:text-taupe-800 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {patient.owner.phone}
                    </a>
                  )}
                  {patient.owner.email && (
                    <a href={`mailto:${patient.owner.email}`} className="text-sm text-taupe-600 hover:text-taupe-800 flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {patient.owner.email}
                    </a>
                  )}
                  {patient.owner.address && (
                    <p className="text-sm text-gray-500">{patient.owner.address}</p>
                  )}
                  <button
                    onClick={() => navigate(`/dashboard/owners/${patient.owner_id}`)}
                    className="text-sm text-taupe-600 hover:text-taupe-800 underline"
                  >
                    Ver ficha del propietario
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            {/* Sección de Vacunaciones */}
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <Syringe className="h-5 w-5 text-green-600" />
                Historial de Vacunación
              </h3>
              {loadingVaccinations ? (
                <div className="text-center py-2 text-gray-500">Cargando...</div>
              ) : vaccinationHistory && vaccinationHistory.length > 0 ? (
                <div className="space-y-2">
                  {vaccinationHistory.map((vac: any) => (
                    <div
                      key={vac.id}
                      className="p-3 bg-green-50 border border-green-200 rounded-lg"
                    >
                      {editingVaccinationId === vac.id ? (
                        <div className="space-y-3">
                          <p className="font-medium text-gray-900">
                            {vac.product_name || 'Vacuna'}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                              label="Fecha de aplicación"
                              type="date"
                              value={editVaccinationData.application_date}
                              onChange={(e) =>
                                setEditVaccinationData({
                                  ...editVaccinationData,
                                  application_date: e.target.value,
                                })
                              }
                            />
                            <Input
                              label="Próxima dosis"
                              type="date"
                              value={editVaccinationData.next_dose_date}
                              onChange={(e) =>
                                setEditVaccinationData({
                                  ...editVaccinationData,
                                  next_dose_date: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Input
                              label="Dosis #"
                              type="number"
                              min="1"
                              value={editVaccinationData.dose_number}
                              onChange={(e) =>
                                setEditVaccinationData({
                                  ...editVaccinationData,
                                  dose_number: e.target.value,
                                })
                              }
                            />
                            <Input
                              label="Cantidad (ml)"
                              type="number"
                              step="0.01"
                              value={editVaccinationData.dose_ml}
                              onChange={(e) =>
                                setEditVaccinationData({
                                  ...editVaccinationData,
                                  dose_ml: e.target.value,
                                })
                              }
                            />
                            <Input
                              label="Lote"
                              value={editVaccinationData.batch_number}
                              onChange={(e) =>
                                setEditVaccinationData({
                                  ...editVaccinationData,
                                  batch_number: e.target.value,
                                })
                              }
                            />
                          </div>
                          <Input
                            label="Notas"
                            value={editVaccinationData.notes}
                            onChange={(e) =>
                              setEditVaccinationData({
                                ...editVaccinationData,
                                notes: e.target.value,
                              })
                            }
                          />
                          <p className="text-xs text-gray-500">
                            Si borrás la próxima dosis, deja de avisar. Si la cambiás, el
                            recordatorio se vuelve a mandar con la fecha nueva.
                          </p>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <Button
                              type="button"
                              variant="danger"
                              loading={deleteVaccination.isPending}
                              onClick={() => handleDeleteVaccination(vac)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Borrar
                            </Button>
                            <div className="flex gap-2 ml-auto">
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setEditingVaccinationId(null)}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                loading={updateVaccination.isPending}
                                onClick={handleSaveVaccination}
                              >
                                Guardar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className="font-medium text-gray-900">
                                {vac.product_name || 'Vacuna'}
                              </span>
                              {vac.dose_number && (
                                <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                                  Dosis #{vac.dose_number}
                                </span>
                              )}
                              <p className="text-sm text-gray-500">
                                {new Date(vac.application_date).toLocaleDateString('es-AR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </p>
                            </div>
                            <div className="flex items-start gap-3">
                              {vac.next_dose_date && (
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Próxima dosis:</p>
                                  <p className="text-sm font-medium text-green-700">
                                    {new Date(vac.next_dose_date).toLocaleDateString('es-AR')}
                                  </p>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => startEditVaccination(vac)}
                                className="p-1.5 rounded-lg text-green-700 hover:bg-green-100"
                                title="Corregir fechas u otros datos"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          {(vac.batch_number || vac.dose_ml) && (
                            <div className="mt-2 flex gap-4 text-xs text-gray-500">
                              {vac.batch_number && <span>Lote: {vac.batch_number}</span>}
                              {vac.dose_ml && <span>Dosis: {vac.dose_ml} ml</span>}
                            </div>
                          )}
                          {vac.notes && (
                            <p className="text-sm text-gray-600 mt-2 italic">{vac.notes}</p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-2">
                  No hay vacunas registradas
                </p>
              )}
            </div>

            {/* Sección de Desparasitaciones */}
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <Bug className="h-5 w-5 text-amber-600" />
                Historial de Desparasitación
              </h3>
              {loadingDewormings ? (
                <div className="text-center py-2 text-gray-500">Cargando...</div>
              ) : dewormingHistory && dewormingHistory.length > 0 ? (
                <div className="space-y-2">
                  {dewormingHistory.map((dew: any) => (
                    <div
                      key={dew.id}
                      className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                    >
                      {editingDewormingId === dew.id ? (
                        <div className="space-y-3">
                          <p className="font-medium text-gray-900">
                            {dew.product_name || 'Antiparasitario'}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                              label="Fecha de aplicación"
                              type="date"
                              value={editDewormingData.application_date}
                              onChange={(e) =>
                                setEditDewormingData({
                                  ...editDewormingData,
                                  application_date: e.target.value,
                                })
                              }
                            />
                            <Input
                              label="Próxima dosis"
                              type="date"
                              value={editDewormingData.next_dose_date}
                              onChange={(e) =>
                                setEditDewormingData({
                                  ...editDewormingData,
                                  next_dose_date: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Select
                              label="Tipo"
                              options={[
                                { value: 'internal', label: 'Interna' },
                                { value: 'external', label: 'Externa' },
                                { value: 'both', label: 'Ambas' },
                              ]}
                              value={editDewormingData.deworming_type}
                              onChange={(e) =>
                                setEditDewormingData({
                                  ...editDewormingData,
                                  deworming_type: e.target.value,
                                })
                              }
                            />
                            <Input
                              label="Dosis"
                              value={editDewormingData.dose}
                              onChange={(e) =>
                                setEditDewormingData({
                                  ...editDewormingData,
                                  dose: e.target.value,
                                })
                              }
                            />
                            <Input
                              label="Lote"
                              value={editDewormingData.batch_number}
                              onChange={(e) =>
                                setEditDewormingData({
                                  ...editDewormingData,
                                  batch_number: e.target.value,
                                })
                              }
                            />
                          </div>
                          <Input
                            label="Notas"
                            value={editDewormingData.notes}
                            onChange={(e) =>
                              setEditDewormingData({
                                ...editDewormingData,
                                notes: e.target.value,
                              })
                            }
                          />
                          <p className="text-xs text-gray-500">
                            Si borrás la próxima dosis, deja de avisar.
                          </p>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <Button
                              type="button"
                              variant="danger"
                              loading={deleteDeworming.isPending}
                              onClick={() => handleDeleteDeworming(dew)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Borrar
                            </Button>
                            <div className="flex gap-2 ml-auto">
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setEditingDewormingId(null)}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                loading={updateDeworming.isPending}
                                onClick={handleSaveDeworming}
                              >
                                Guardar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className="font-medium text-gray-900">
                                {dew.product_name || 'Antiparasitario'}
                              </span>
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                dew.deworming_type === 'internal'
                                  ? 'bg-blue-100 text-blue-700'
                                  : dew.deworming_type === 'external'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-amber-200 text-amber-800'
                              }`}>
                                {dew.deworming_type === 'internal' ? 'Interna' :
                                 dew.deworming_type === 'external' ? 'Externa' : 'Ambas'}
                              </span>
                              <p className="text-sm text-gray-500">
                                {new Date(dew.application_date).toLocaleDateString('es-AR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </p>
                            </div>
                            <div className="flex items-start gap-3">
                              {dew.next_dose_date && (
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Próxima dosis:</p>
                                  <p className="text-sm font-medium text-amber-700">
                                    {new Date(dew.next_dose_date).toLocaleDateString('es-AR')}
                                  </p>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => startEditDeworming(dew)}
                                className="p-1.5 rounded-lg text-amber-700 hover:bg-amber-100"
                                title="Corregir fechas u otros datos"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          {(dew.batch_number || dew.dose) && (
                            <div className="mt-2 flex gap-4 text-xs text-gray-500">
                              {dew.batch_number && <span>Lote: {dew.batch_number}</span>}
                              {dew.dose && <span>Dosis: {dew.dose}</span>}
                            </div>
                          )}
                          {dew.notes && (
                            <p className="text-sm text-gray-600 mt-2 italic">{dew.notes}</p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-2">
                  No hay desparasitaciones registradas
                </p>
              )}
            </div>

            {/* Sección de Consultas Médicas */}
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <Stethoscope className="h-5 w-5 text-taupe-600" />
                Historial de Consultas
              </h3>
              {loadingRecords ? (
                <div className="text-center py-4">Cargando historial...</div>
              ) : medicalRecords && medicalRecords.length > 0 ? (
                <div className="space-y-3">
                  {medicalRecords.map((record) => (
                    <div key={record.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="flex items-center bg-gray-50">
                        <button
                          onClick={() => setExpandedRecord(expandedRecord === record.id ? null : record.id)}
                          className="flex-1 px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Stethoscope className="h-5 w-5 text-taupe-600" />
                            <div className="text-left">
                              <p className="font-medium text-gray-900">
                                {record.reason || 'Consulta general'}
                              </p>
                              <p className="text-sm text-gray-500">{formatDate(record.visit_date)}</p>
                            </div>
                          </div>
                          {expandedRecord === record.id ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditRecord(record);
                          }}
                          className="px-3 py-3 text-gray-400 hover:text-taupe-600 hover:bg-taupe-50 transition-colors"
                          title="Editar registro"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>

                      {expandedRecord === record.id && (
                        <div className="px-4 py-3 space-y-3 text-sm">
                          {record.symptoms && (
                            <div>
                              <p className="text-gray-500 font-medium">Examen clínico:</p>
                              <p className="text-gray-700">{record.symptoms}</p>
                            </div>
                          )}
                          {record.diagnosis && (
                            <div>
                              <p className="text-gray-500 font-medium">Diagnóstico:</p>
                              <p className="text-gray-700">{record.diagnosis}</p>
                            </div>
                          )}
                          {record.treatment && (
                            <div>
                              <p className="text-gray-500 font-medium">Tratamiento:</p>
                              <p className="text-gray-700">{record.treatment}</p>
                            </div>
                          )}
                          {record.prescription && (
                            <div className="flex items-start gap-2">
                              <Pill className="h-4 w-4 text-cream-600 mt-0.5" />
                              <div>
                                <p className="text-gray-500 font-medium">Receta:</p>
                                <p className="text-gray-700">{record.prescription}</p>
                              </div>
                            </div>
                          )}
                          <div className="flex gap-4 text-gray-600">
                            {record.weight && (
                              <span className="flex items-center gap-1">
                                <Scale className="h-4 w-4" /> {record.weight} kg
                              </span>
                            )}
                            {record.temperature && (
                              <span className="flex items-center gap-1">
                                <Thermometer className="h-4 w-4" /> {record.temperature}°C
                              </span>
                            )}
                          </div>
                          {record.next_visit_date && (
                            <div className="mt-2 p-2 bg-cream-50 rounded">
                              <p className="text-cream-800 text-sm">
                                <strong>Próxima visita:</strong> {record.next_visit_date}
                                {record.next_visit_reason && ` - ${record.next_visit_reason}`}
                              </p>
                            </div>
                          )}

                          {/* Comanda asociada */}
                          {record.service_order && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ShoppingCart className="h-4 w-4 text-blue-600" />
                                  <span className="font-medium text-blue-800">
                                    Comanda #{record.service_order.id}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    record.service_order.status === 'charged'
                                      ? 'bg-green-100 text-green-700'
                                      : record.service_order.status === 'cancelled'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {record.service_order.status === 'charged' ? 'Cobrada' :
                                     record.service_order.status === 'cancelled' ? 'Cancelada' :
                                     record.service_order.status === 'ready_to_charge' ? 'Lista para cobrar' : 'Abierta'}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-green-600">
                                    {formatPrice(record.service_order.total)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {record.service_order.items_count} item{record.service_order.items_count !== 1 ? 's' : ''}
                                  </p>
                                </div>
                              </div>
                              {record.service_order.charged_at && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Cobrada el {new Date(record.service_order.charged_at).toLocaleDateString('es-AR')}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Archivos adjuntos */}
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-gray-500 font-medium flex items-center gap-1">
                                <Paperclip className="h-4 w-4" /> Archivos adjuntos
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setUploadingToRecord(record.id)}
                              >
                                <Upload className="h-4 w-4 mr-1" />
                                Subir
                              </Button>
                            </div>

                            {/* Formulario de subida */}
                            {uploadingToRecord === record.id && (
                              <div className="mb-3 p-3 bg-gray-50 rounded-lg space-y-2">
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Descripción (opcional)"
                                    value={uploadDescription}
                                    onChange={(e) => setUploadDescription(e.target.value)}
                                    className="flex-1"
                                  />
                                  <Select
                                    options={attachmentTypeOptions}
                                    value={uploadType}
                                    onChange={(e) => setUploadType(e.target.value)}
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={handleFileSelect}
                                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                                    className="flex-1 text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-taupe-100 file:text-taupe-700 hover:file:bg-taupe-200"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setUploadingToRecord(null);
                                      setUploadDescription('');
                                      setUploadType('');
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                                {uploadAttachment.isPending && (
                                  <p className="text-sm text-gray-500">Subiendo archivo...</p>
                                )}
                              </div>
                            )}

                            {/* Lista de archivos */}
                            {currentAttachments && currentAttachments.length > 0 ? (
                              <div className="space-y-2">
                                {currentAttachments.map((attachment) => {
                                  const FileIcon = getFileIcon(attachment.file_type);
                                  return (
                                    <div
                                      key={attachment.id}
                                      className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <FileIcon className="h-5 w-5 text-taupe-500 flex-shrink-0" />
                                        <div className="min-w-0">
                                          <p className="text-sm font-medium text-gray-700 truncate">
                                            {attachment.description || attachment.file_name}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {getAttachmentTypeLabel(attachment.attachment_type)}
                                            {attachment.file_size && ` - ${formatFileSize(attachment.file_size)}`}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => handleDownloadAttachment(attachment)}
                                          className="p-1.5 text-gray-500 hover:text-taupe-600 hover:bg-taupe-50 rounded"
                                          title="Descargar"
                                        >
                                          <Download className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteAttachment(attachment.id)}
                                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                          title="Eliminar"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400">No hay archivos adjuntos</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No hay registros médicos
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-6">
            {/* Servicios de Peluquería */}
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <Scissors className="h-5 w-5 text-pink-500" />
                Servicios de Peluquería
              </h3>
              {loadingGrooming ? (
                <div className="text-center py-4 text-gray-500">Cargando...</div>
              ) : groomingAppointments && groomingAppointments.length > 0 ? (
                <div className="space-y-2">
                  {groomingAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="p-3 bg-pink-50 border border-pink-200 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-gray-900">
                            {SERVICE_TYPE_LABELS[apt.service_type]}
                          </span>
                          <p className="text-sm text-gray-500">
                            {new Date(apt.scheduled_date).toLocaleDateString('es-AR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })} - {apt.scheduled_time.slice(0, 5)}
                          </p>
                        </div>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {STATUS_LABELS[apt.status]}
                        </span>
                      </div>
                      {apt.notes && (
                        <p className="text-sm text-gray-600 mt-2 italic">{apt.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  No hay servicios de peluquería registrados
                </p>
              )}
            </div>

            {/* Todas las Comandas */}
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <ShoppingCart className="h-5 w-5 text-blue-500" />
                Historial de Comandas
              </h3>
              {loadingOrders ? (
                <div className="text-center py-4 text-gray-500">Cargando...</div>
              ) : patientServiceOrders && patientServiceOrders.length > 0 ? (
                <div className="space-y-2">
                  {patientServiceOrders.map((order) => (
                    <div
                      key={order.id}
                      className="p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div>
                            <span className="font-medium text-gray-900">
                              Comanda #{order.id}
                            </span>
                            <p className="text-sm text-gray-500">
                              {new Date(order.created_at).toLocaleDateString('es-AR', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${ORDER_STATUS_COLORS[order.status]}`}>
                            {ORDER_STATUS_LABELS[order.status]}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-bold text-green-600">
                              {formatPrice(order.total)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              onClose();
                              navigate(`/dashboard/service-orders?edit=${order.id}`);
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ver comanda"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {/* Items de la comanda (todos) */}
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex flex-wrap gap-1">
                          {order.items.map((item) => (
                            <span
                              key={item.id}
                              className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                            >
                              {item.product_name || 'Item'}
                              {item.quantity && item.quantity > 1 ? ` x${item.quantity}` : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                      {order.notes && (
                        <p className="text-xs text-gray-500 mt-2 italic">{order.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  No hay comandas registradas
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'new-record' && (
          <div className="space-y-5">
            {/* Peso y Temperatura PRIMERO - Datos vitales */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-xl border border-blue-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Scale className="h-4 w-4 text-blue-600" />
                Datos Vitales
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Peso (kg)
                  </label>
                  <div className="relative">
                    <Scale className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-500" />
                    <input
                      type="number"
                      step="0.1"
                      value={newRecord.weight}
                      onChange={(e) => setNewRecord({ ...newRecord, weight: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 text-lg font-medium border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none bg-white"
                      placeholder="0.0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Temperatura (°C)
                  </label>
                  <div className="relative">
                    <Thermometer className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                    <input
                      type="number"
                      step="0.1"
                      value={newRecord.temperature}
                      onChange={(e) => setNewRecord({ ...newRecord, temperature: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 text-lg font-medium border border-green-200 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none bg-white"
                      placeholder="38.5"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Motivo de consulta */}
            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Motivo de consulta / Anamnesis
              </label>
              <textarea
                value={newRecord.reason}
                onChange={(e) => {
                  setNewRecord({ ...newRecord, reason: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={2}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Ej: Control, Vacunación, Emergencia, motivo de la consulta..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Examen clínico
              </label>
              <textarea
                value={newRecord.symptoms}
                onChange={(e) => {
                  setNewRecord({ ...newRecord, symptoms: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={3}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Hallazgos del examen clínico..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Diagnóstico
              </label>
              <textarea
                value={newRecord.diagnosis}
                onChange={(e) => {
                  setNewRecord({ ...newRecord, diagnosis: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={3}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Diagnóstico presuntivo o definitivo..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Tratamiento
              </label>
              <textarea
                value={newRecord.treatment}
                onChange={(e) => {
                  setNewRecord({ ...newRecord, treatment: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={3}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Tratamiento aplicado o indicado..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Receta / Prescripción
              </label>
              <textarea
                value={newRecord.prescription}
                onChange={(e) => {
                  setNewRecord({ ...newRecord, prescription: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={3}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Medicamentos recetados con dosis e indicaciones..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                type="date"
                label="Próxima visita"
                value={newRecord.next_visit_date}
                onChange={(e) => setNewRecord({ ...newRecord, next_visit_date: e.target.value })}
              />
              <Input
                label="Motivo próxima visita"
                value={newRecord.next_visit_reason}
                onChange={(e) => setNewRecord({ ...newRecord, next_visit_reason: e.target.value })}
                placeholder="Ej: Control, Vacuna..."
              />
            </div>

            {/* Sección de Vacunación */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowVaccinationSection(!showVaccinationSection)}
                className="w-full px-4 py-3 flex items-center justify-between bg-green-50 hover:bg-green-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Syringe className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">Vacunación</span>
                  {vaccinations.length > 0 && (
                    <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                      {vaccinations.length} {vaccinations.length === 1 ? 'vacuna' : 'vacunas'}
                    </span>
                  )}
                </div>
                {showVaccinationSection ? (
                  <ChevronUp className="h-5 w-5 text-green-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-green-600" />
                )}
              </button>

              {showVaccinationSection && (
                <div className="p-4 space-y-4 bg-white">
                  {/* Lista de vacunas agregadas */}
                  {vaccinations.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <p className="text-sm font-medium text-gray-700">Vacunas agregadas:</p>
                      {vaccinations.map((vac, index) => (
                        <div key={index} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Syringe className="h-4 w-4 text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{vac.product_name}</p>
                              <p className="text-xs text-gray-600">
                                {vac.batch_number && `Lote: ${vac.batch_number} • `}
                                Dosis {vac.dose_number}
                                {vac.dose_ml && ` • ${vac.dose_ml}ml`}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveVaccine(index)}
                            className="text-red-600 hover:text-red-700 p-1"
                            title="Eliminar"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Buscador de vacunas */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vacuna aplicada
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={vaccineSearch}
                        onChange={(e) => {
                          setVaccineSearch(e.target.value);
                          setShowVaccineSearch(true);
                        }}
                        onFocus={() => setShowVaccineSearch(true)}
                        placeholder="Buscar vacuna..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none"
                      />
                    </div>

                    {/* Dropdown de resultados */}
                    {showVaccineSearch && filteredVaccines.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                        {filteredVaccines.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => {
                              setVaccinationData({ ...vaccinationData, product_id: String(product.id) });
                              setVaccineSearch(product.name);
                              setShowVaccineSearch(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Syringe className="h-4 w-4 text-green-500" />
                            <span className="text-sm">{product.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      label="Lote"
                      value={vaccinationData.batch_number}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, batch_number: e.target.value })}
                      placeholder="Número de lote"
                    />
                    <Input
                      type="number"
                      min="1"
                      label="N° Dosis"
                      value={vaccinationData.dose_number}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, dose_number: e.target.value })}
                    />
                    <Input
                      type="number"
                      step="0.1"
                      label="Dosis (ml)"
                      value={vaccinationData.dose_ml}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, dose_ml: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="date"
                      label="Próxima dosis"
                      value={vaccinationData.next_dose_date}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, next_dose_date: e.target.value })}
                    />
                    <Input
                      label="Notas próxima dosis"
                      value={vaccinationData.next_dose_notes}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, next_dose_notes: e.target.value })}
                      placeholder="Ej: Refuerzo anual"
                    />
                  </div>

                  <Input
                    label="Observaciones"
                    value={vaccinationData.notes}
                    onChange={(e) => setVaccinationData({ ...vaccinationData, notes: e.target.value })}
                    placeholder="Observaciones adicionales..."
                  />

                  {/* Botón para agregar vacuna a la lista */}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAddVaccine}
                    disabled={!vaccinationData.product_id}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar vacuna
                  </Button>
                </div>
              )}
            </div>

            {/* Sección de Desparasitación */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowDewormingSection(!showDewormingSection)}
                className="w-full px-4 py-3 flex items-center justify-between bg-amber-50 hover:bg-amber-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Bug className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-800">Desparasitación</span>
                  {dewormings.length > 0 && (
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                      {dewormings.length} agregada{dewormings.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {showDewormingSection ? (
                  <ChevronUp className="h-5 w-5 text-amber-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-amber-600" />
                )}
              </button>

              {showDewormingSection && (
                <div className="p-4 space-y-4 bg-white">
                  {/* Lista de desparasitaciones agregadas */}
                  {dewormings.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <p className="text-sm font-medium text-gray-700">Desparasitaciones agregadas:</p>
                      {dewormings.map((dew, index) => (
                        <div key={index} className="flex items-center justify-between bg-amber-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Bug className="h-4 w-4 text-amber-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {dew.product_name}
                                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                  dew.deworming_type === 'internal'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {dew.deworming_type === 'internal' ? 'Interna' : 'Externa'}
                                </span>
                              </p>
                              <p className="text-xs text-gray-600">
                                {dew.next_dose_date && `Próx. dosis: ${dew.next_dose_date}`}
                                {dew.dose && ` • ${dew.dose}`}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveDeworming(index)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tipo de desparasitación */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de desparasitación
                    </label>
                    <div className="flex gap-3">
                      {[
                        { value: 'internal', label: 'Interna' },
                        { value: 'external', label: 'Externa' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setDewormingData({ ...dewormingData, deworming_type: option.value as any })}
                          className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            dewormingData.deworming_type === option.value
                              ? 'bg-amber-100 border-amber-400 text-amber-800'
                              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Input
                    label="Producto/Medicamento"
                    value={dewormingData.product_name}
                    onChange={(e) => setDewormingData({ ...dewormingData, product_name: e.target.value })}
                    placeholder="Nombre del antiparasitario"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="date"
                      label="Próxima dosis"
                      value={dewormingData.next_dose_date}
                      onChange={(e) => setDewormingData({ ...dewormingData, next_dose_date: e.target.value })}
                    />
                    <Input
                      label="Notas próxima dosis"
                      value={dewormingData.next_dose_notes}
                      onChange={(e) => setDewormingData({ ...dewormingData, next_dose_notes: e.target.value })}
                      placeholder="Ej: Repetir en 3 meses"
                    />
                  </div>

                  <Input
                    label="Observaciones"
                    value={dewormingData.notes}
                    onChange={(e) => setDewormingData({ ...dewormingData, notes: e.target.value })}
                    placeholder="Observaciones adicionales..."
                  />

                  {/* Botón para agregar desparasitación a la lista */}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAddDeworming}
                    disabled={!dewormingData.product_name.trim()}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar desparasitación
                  </Button>
                </div>
              )}
            </div>

            {/* Sección de Comanda */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-cream-600" />
                  Comanda - Productos/Servicios
                </h3>
                {orderItems.length > 0 && (
                  <span className="text-sm font-medium text-taupe-600">
                    Total a cobrar: {formatPrice(orderTotal)}
                  </span>
                )}
              </div>

              {/* Buscador de productos y botones */}
              <div className="relative mb-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setShowProductSearch(true);
                      }}
                      onFocus={() => setShowProductSearch(true)}
                      placeholder="Buscar producto o servicio..."
                      className="w-full pl-10 pr-4 py-2 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCatalogModal(true)}
                    className="px-3 py-2 text-sm bg-taupe-100 text-taupe-700 rounded-xl hover:bg-taupe-200 flex items-center gap-1"
                    title="Ver catálogo completo"
                  >
                    <Grid className="h-4 w-4" />
                    <span className="hidden sm:inline">Catálogo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCustomForm(!showCustomForm)}
                    className="px-3 py-2 text-sm bg-cream-100 text-cream-700 rounded-xl hover:bg-cream-200 flex items-center gap-1"
                    title="Agregar item personalizado"
                  >
                    <PlusCircle className="h-4 w-4" />
                    <span className="hidden sm:inline">Otro</span>
                  </button>
                </div>

                {/* Formulario item personalizado */}
                {showCustomForm && (
                  <div className="mt-2 p-3 bg-cream-50 rounded-lg border border-cream-200">
                    <p className="text-xs text-gray-500 mb-2">Agregar producto/servicio no listado:</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customItem.name}
                        onChange={(e) => setCustomItem({ ...customItem, name: e.target.value })}
                        placeholder="Nombre del item..."
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-taupe-400 outline-none"
                      />
                      <div className="relative w-28">
                        <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={customItem.price}
                          onChange={(e) => setCustomItem({ ...customItem, price: e.target.value })}
                          placeholder="Precio"
                          className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg text-right focus:ring-1 focus:ring-taupe-400 outline-none"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleAddCustomItem}
                        disabled={!customItem.name.trim()}
                        className="px-3 py-1.5 text-sm bg-taupe-600 text-white rounded-lg hover:bg-taupe-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Agregar
                      </button>
                    </div>
                  </div>
                )}

                {/* Formulario para producto sell_by_ml */}
                {mlProductPending && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-sm text-gray-900">{mlProductPending.name}</span>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                        {formatPrice(mlProductPending.price_per_ml ?? 0)}/ml
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Cantidad:</span>
                      <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={mlQuantityInput}
                        onChange={(e) => setMlQuantityInput(e.target.value)}
                        placeholder="0"
                        className="w-20 px-2 py-1.5 text-sm border border-blue-300 rounded-lg text-center focus:ring-1 focus:ring-blue-400 outline-none"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleConfirmMlProduct();
                          if (e.key === 'Escape') handleCancelMlProduct();
                        }}
                      />
                      <span className="text-xs text-gray-500">ml</span>
                      <span className="text-xs text-gray-400">=</span>
                      <span className="text-sm font-bold text-green-600">
                        {mlQuantityInput && !isNaN(parseFloat(mlQuantityInput))
                          ? formatPrice(parseFloat(mlQuantityInput) * (mlProductPending.price_per_ml ?? 0))
                          : '$0'}
                      </span>
                      <div className="flex gap-1 ml-auto">
                        <button
                          type="button"
                          onClick={handleConfirmMlProduct}
                          disabled={!mlQuantityInput || parseFloat(mlQuantityInput) <= 0}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Agregar
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelMlProduct}
                          className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Dropdown de resultados - se abre hacia arriba */}
                {showProductSearch && filteredProducts.length > 0 && (
                  <div className="absolute z-50 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                    {filteredProducts.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleAddProduct(product)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{product.name}</span>
                          {product.code && (
                            <span className="text-xs text-gray-400">({product.code})</span>
                          )}
                        </div>
                        <span className="text-sm font-medium text-taupe-600">
                          {formatPrice(product.sale_price)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Lista de items agregados */}
              {orderItems.length > 0 ? (
                <div className="space-y-2">
                  {orderItems.map((item) => (
                    <div
                      key={item.id}
                      className={`p-3 rounded-lg border ${
                        item.should_charge
                          ? 'bg-white border-gray-200'
                          : 'bg-gray-50 border-gray-100'
                      } ${item.is_custom ? 'border-l-4 border-l-cream-400' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox cobrar */}
                        <button
                          onClick={() => handleUpdateItem(item.id, 'should_charge', !item.should_charge)}
                          className={`mt-1 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center ${
                            item.should_charge
                              ? 'bg-taupe-600 border-taupe-600 text-white'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          {item.should_charge && <Check className="h-3 w-3" />}
                        </button>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium text-sm ${!item.should_charge && 'text-gray-400'}`}>
                                {item.product_name}
                              </span>
                              {item.is_custom && (
                                <span className="text-xs px-1.5 py-0.5 bg-cream-100 text-cream-700 rounded">
                                  Personalizado
                                </span>
                              )}
                              {item.is_ml_product && (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                  {item.ml_quantity} ml
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="flex items-center gap-3 mt-2">
                            {/* Cantidad - ocultar para productos por ml */}
                            {!item.is_ml_product && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Cant:</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => handleUpdateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                                  className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-center"
                                  disabled={!item.should_charge}
                                />
                              </div>
                            )}

                            {/* Info de precio por ml para productos sell_by_ml */}
                            {item.is_ml_product && item.price_per_ml && (
                              <span className="text-xs text-gray-500">
                                {formatPrice(item.price_per_ml)}/ml × {item.ml_quantity} ml
                              </span>
                            )}

                            {/* Precio unitario - solo para productos normales */}
                            {!item.is_ml_product && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Precio:</span>
                                <div className="relative">
                                  <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.unit_price}
                                    onChange={(e) => handleUpdateItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                                    className={`w-24 pl-6 pr-2 py-1 text-sm border rounded text-right ${
                                      item.unit_price !== item.reference_price && !item.is_custom
                                        ? 'border-yellow-400 bg-yellow-50'
                                        : 'border-gray-200'
                                    }`}
                                    disabled={!item.should_charge}
                                  />
                                </div>
                                {item.unit_price !== item.reference_price && !item.is_custom && (
                                  <span className="text-xs text-gray-400 line-through">
                                    {formatPrice(item.reference_price)}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Subtotal */}
                            <span className={`text-sm font-medium ml-auto ${!item.should_charge && 'text-gray-400'}`}>
                              {formatPrice(item.quantity * item.unit_price)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  Busca y agrega productos o servicios para crear la comanda
                </p>
              )}
            </div>

            <div className="flex justify-end pt-4 gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setNewRecord({
                    reason: '',
                    symptoms: '',
                    diagnosis: '',
                    treatment: '',
                    prescription: '',
                    weight: '',
                    temperature: '',
                    next_visit_date: '',
                    next_visit_reason: '',
                    notes: '',
                  });
                  setOrderItems([]);
                  setVaccinations([]);
                  setVaccinationData({
                    product_id: '',
                    batch_number: '',
                    dose_number: '1',
                    dose_ml: '',
                    next_dose_date: '',
                    next_dose_notes: '',
                    notes: '',
                  });
                  setDewormings([]);
                  setDewormingData({
                    product_id: '',
                    product_name: '',
                    deworming_type: 'internal',
                    batch_number: '',
                    dose: '',
                    next_dose_date: '',
                    next_dose_notes: '',
                    notes: '',
                  });
                  setVaccineSearch('');
                  setShowVaccinationSection(false);
                  setShowDewormingSection(false);
                }}
              >
                Limpiar
              </Button>
              <Button
                onClick={handleCreateRecord}
                loading={createRecord.isPending || createServiceOrder.isPending || createVaccination.isPending || createDeworming.isPending}
              >
                <Plus className="h-4 w-4 mr-2" />
                {(() => {
                  const hasItems = orderItems.filter(i => i.should_charge).length > 0;
                  const hasVaccine = vaccinations.length > 0;
                  const hasDeworming = !!dewormingData.product_name;
                  const extras = [];
                  if (hasVaccine) extras.push(vaccinations.length > 1 ? `${vaccinations.length} Vacunas` : 'Vacuna');
                  if (hasDeworming) extras.push('Desparasitación');
                  if (hasItems) extras.push('Comanda');
                  return extras.length > 0
                    ? `Guardar Consulta + ${extras.join(', ')}`
                    : 'Guardar Consulta';
                })()}
              </Button>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Modal de Edición de Registro Médico */}
      <Modal
        isOpen={!!editingRecord}
        onClose={() => setEditingRecord(null)}
        title="Editar Registro Médico"
        size="full"
      >
        {editingRecord && (
          <div className="space-y-5 max-w-4xl mx-auto">
            {/* Peso y Temperatura PRIMERO - Datos vitales */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-xl border border-blue-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Scale className="h-4 w-4 text-blue-600" />
                Datos Vitales
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Peso (kg)
                  </label>
                  <div className="relative">
                    <Scale className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-500" />
                    <input
                      type="number"
                      step="0.1"
                      value={editingRecord.weight}
                      onChange={(e) => setEditingRecord({ ...editingRecord, weight: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 text-lg font-medium border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none bg-white"
                      placeholder="0.0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Temperatura (°C)
                  </label>
                  <div className="relative">
                    <Thermometer className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                    <input
                      type="number"
                      step="0.1"
                      value={editingRecord.temperature}
                      onChange={(e) => setEditingRecord({ ...editingRecord, temperature: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 text-lg font-medium border border-green-200 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none bg-white"
                      placeholder="38.5"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Motivo de consulta */}
            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Motivo de consulta / Anamnesis
              </label>
              <textarea
                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                value={editingRecord.reason || ''}
                onChange={(e) => {
                  setEditingRecord({ ...editingRecord, reason: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={2}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Ej: Control, Vacunación, Emergencia, motivo de la consulta..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Examen clínico
              </label>
              <textarea
                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                value={editingRecord.symptoms || ''}
                onChange={(e) => {
                  setEditingRecord({ ...editingRecord, symptoms: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={3}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Hallazgos del examen clínico..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Diagnóstico
              </label>
              <textarea
                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                value={editingRecord.diagnosis || ''}
                onChange={(e) => {
                  setEditingRecord({ ...editingRecord, diagnosis: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={3}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Diagnóstico presuntivo o definitivo..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Tratamiento
              </label>
              <textarea
                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                value={editingRecord.treatment || ''}
                onChange={(e) => {
                  setEditingRecord({ ...editingRecord, treatment: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={3}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Tratamiento aplicado o indicado..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Receta / Prescripción
              </label>
              <textarea
                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                value={editingRecord.prescription || ''}
                onChange={(e) => {
                  setEditingRecord({ ...editingRecord, prescription: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={3}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Medicamentos recetados..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                type="date"
                label="Próxima visita"
                value={editingRecord.next_visit_date || ''}
                onChange={(e) => setEditingRecord({ ...editingRecord, next_visit_date: e.target.value })}
              />
              <Input
                label="Motivo próxima visita"
                value={editingRecord.next_visit_reason || ''}
                onChange={(e) => setEditingRecord({ ...editingRecord, next_visit_reason: e.target.value })}
                placeholder="Ej: Control, Vacuna..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-taupe-700 mb-1.5">
                Notas adicionales
              </label>
              <textarea
                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                value={editingRecord.notes || ''}
                onChange={(e) => {
                  setEditingRecord({ ...editingRecord, notes: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                rows={2}
                className="w-full px-4 py-3 border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-base resize-none overflow-hidden"
                placeholder="Notas adicionales..."
              />
            </div>

            {/* Sección de Vacunación */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowVaccinationSection(!showVaccinationSection)}
                className="w-full px-4 py-3 flex items-center justify-between bg-green-50 hover:bg-green-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Syringe className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">Vacunación</span>
                  {vaccinations.length > 0 && (
                    <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                      {vaccinations.length} {vaccinations.length === 1 ? 'vacuna' : 'vacunas'}
                    </span>
                  )}
                </div>
                {showVaccinationSection ? (
                  <ChevronUp className="h-5 w-5 text-green-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-green-600" />
                )}
              </button>

              {showVaccinationSection && (
                <div className="p-4 space-y-4 bg-white">
                  {/* Lista de vacunas agregadas */}
                  {vaccinations.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <p className="text-sm font-medium text-gray-700">Vacunas agregadas:</p>
                      {vaccinations.map((vac, index) => (
                        <div key={index} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Syringe className="h-4 w-4 text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{vac.product_name}</p>
                              <p className="text-xs text-gray-600">
                                {vac.batch_number && `Lote: ${vac.batch_number} • `}
                                Dosis {vac.dose_number}
                                {vac.dose_ml && ` • ${vac.dose_ml}ml`}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveVaccine(index)}
                            className="text-red-600 hover:text-red-700 p-1"
                            title="Eliminar"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Buscador de vacunas */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vacuna aplicada
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={vaccineSearch}
                        onChange={(e) => {
                          setVaccineSearch(e.target.value);
                          setShowVaccineSearch(true);
                        }}
                        onFocus={() => setShowVaccineSearch(true)}
                        placeholder="Buscar vacuna..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none"
                      />
                    </div>

                    {/* Dropdown de resultados */}
                    {showVaccineSearch && filteredVaccines.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                        {filteredVaccines.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => {
                              setVaccinationData({ ...vaccinationData, product_id: String(product.id) });
                              setVaccineSearch(product.name);
                              setShowVaccineSearch(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Syringe className="h-4 w-4 text-green-500" />
                            <span className="text-sm">{product.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      label="Lote"
                      value={vaccinationData.batch_number}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, batch_number: e.target.value })}
                      placeholder="Número de lote"
                    />
                    <Input
                      type="number"
                      min="1"
                      label="N° Dosis"
                      value={vaccinationData.dose_number}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, dose_number: e.target.value })}
                    />
                    <Input
                      type="number"
                      step="0.1"
                      label="Dosis (ml)"
                      value={vaccinationData.dose_ml}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, dose_ml: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="date"
                      label="Próxima dosis"
                      value={vaccinationData.next_dose_date}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, next_dose_date: e.target.value })}
                    />
                    <Input
                      label="Notas próxima dosis"
                      value={vaccinationData.next_dose_notes}
                      onChange={(e) => setVaccinationData({ ...vaccinationData, next_dose_notes: e.target.value })}
                      placeholder="Ej: Refuerzo anual"
                    />
                  </div>

                  <Input
                    label="Observaciones"
                    value={vaccinationData.notes}
                    onChange={(e) => setVaccinationData({ ...vaccinationData, notes: e.target.value })}
                    placeholder="Observaciones adicionales..."
                  />

                  {/* Botón para agregar vacuna a la lista */}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAddVaccine}
                    disabled={!vaccinationData.product_id}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar vacuna
                  </Button>
                </div>
              )}
            </div>

            {/* Sección de Desparasitación */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowDewormingSection(!showDewormingSection)}
                className="w-full px-4 py-3 flex items-center justify-between bg-amber-50 hover:bg-amber-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Bug className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-800">Desparasitación</span>
                  {dewormings.length > 0 && (
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                      {dewormings.length} agregada{dewormings.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {showDewormingSection ? (
                  <ChevronUp className="h-5 w-5 text-amber-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-amber-600" />
                )}
              </button>

              {showDewormingSection && (
                <div className="p-4 space-y-4 bg-white">
                  {/* Lista de desparasitaciones agregadas */}
                  {dewormings.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <p className="text-sm font-medium text-gray-700">Desparasitaciones agregadas:</p>
                      {dewormings.map((dew, index) => (
                        <div key={index} className="flex items-center justify-between bg-amber-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Bug className="h-4 w-4 text-amber-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {dew.product_name}
                                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                  dew.deworming_type === 'internal'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {dew.deworming_type === 'internal' ? 'Interna' : 'Externa'}
                                </span>
                              </p>
                              <p className="text-xs text-gray-600">
                                {dew.next_dose_date && `Próx. dosis: ${dew.next_dose_date}`}
                                {dew.dose && ` • ${dew.dose}`}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveDeworming(index)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tipo de desparasitación */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de desparasitación
                    </label>
                    <div className="flex gap-3">
                      {[
                        { value: 'internal', label: 'Interna' },
                        { value: 'external', label: 'Externa' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setDewormingData({ ...dewormingData, deworming_type: option.value as any })}
                          className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            dewormingData.deworming_type === option.value
                              ? 'bg-amber-100 border-amber-400 text-amber-800'
                              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Input
                    label="Producto/Medicamento"
                    value={dewormingData.product_name}
                    onChange={(e) => setDewormingData({ ...dewormingData, product_name: e.target.value })}
                    placeholder="Nombre del producto"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Lote"
                      value={dewormingData.batch_number}
                      onChange={(e) => setDewormingData({ ...dewormingData, batch_number: e.target.value })}
                      placeholder="Número de lote"
                    />
                    <Input
                      label="Dosis aplicada"
                      value={dewormingData.dose}
                      onChange={(e) => setDewormingData({ ...dewormingData, dose: e.target.value })}
                      placeholder="Ej: 5ml, 1 comprimido"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="date"
                      label="Próxima dosis"
                      value={dewormingData.next_dose_date}
                      onChange={(e) => setDewormingData({ ...dewormingData, next_dose_date: e.target.value })}
                    />
                    <Input
                      label="Notas próxima dosis"
                      value={dewormingData.next_dose_notes}
                      onChange={(e) => setDewormingData({ ...dewormingData, next_dose_notes: e.target.value })}
                      placeholder="Ej: Desparasitación mensual"
                    />
                  </div>

                  <Input
                    label="Observaciones"
                    value={dewormingData.notes}
                    onChange={(e) => setDewormingData({ ...dewormingData, notes: e.target.value })}
                    placeholder="Observaciones adicionales..."
                  />

                  {/* Botón para agregar desparasitación a la lista */}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAddDeworming}
                    disabled={!dewormingData.product_name.trim()}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar desparasitación
                  </Button>
                </div>
              )}
            </div>

            {/* Sección de Comanda */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowOrderSection(!showOrderSection)}
                className="w-full px-4 py-3 flex items-center justify-between bg-purple-50 hover:bg-purple-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-purple-600" />
                  <span className="font-medium text-purple-800">Comanda / Items a cobrar</span>
                  {orderItems.filter(i => i.should_charge).length > 0 && (
                    <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                      {orderItems.filter(i => i.should_charge).length} item(s) - {formatPrice(orderTotal)}
                    </span>
                  )}
                </div>
                {showOrderSection ? (
                  <ChevronUp className="h-5 w-5 text-purple-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-purple-600" />
                )}
              </button>

              {showOrderSection && (
                <div className="p-4 space-y-4 bg-white">
                  {/* Buscador de productos */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Agregar producto/servicio
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={productSearch}
                          onChange={(e) => {
                            setProductSearch(e.target.value);
                            setShowProductSearch(true);
                          }}
                          onFocus={() => setShowProductSearch(true)}
                          placeholder="Buscar producto..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none"
                        />
                        {/* Dropdown de resultados */}
                        {showProductSearch && filteredProducts.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                            {filteredProducts.map((product) => (
                              <button
                                key={product.id}
                                type="button"
                                onClick={() => {
                                  handleAddProduct(product);
                                  setProductSearch('');
                                  setShowProductSearch(false);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-0"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{product.name}</p>
                                    <p className="text-xs text-gray-500">{product.category?.name}</p>
                                  </div>
                                  <span className="text-sm font-medium text-green-600">
                                    {formatPrice(Number(product.sale_price))}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setShowCatalogModal(true)}
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Lista de items */}
                  {orderItems.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Items agregados:</p>
                      {orderItems.map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={item.should_charge}
                                onChange={(e) => handleUpdateItem(item.id, 'should_charge', e.target.checked)}
                                className="rounded"
                              />
                              <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                            </div>
                            <div className="flex items-center gap-3 mt-1 ml-6">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleUpdateItem(item.id, 'quantity', Math.max(1, item.quantity - 1))}
                                  className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300"
                                >
                                  -
                                </button>
                                <span className="text-sm w-8 text-center">{item.quantity}</span>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateItem(item.id, 'quantity', item.quantity + 1)}
                                  className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300"
                                >
                                  +
                                </button>
                              </div>
                              <span className="text-sm text-gray-600">
                                {formatPrice(item.unit_price)} × {item.quantity} = {formatPrice(item.unit_price * item.quantity)}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div className="text-right pt-2 border-t">
                        <p className="text-sm text-gray-600">
                          Total a cobrar: <span className="text-lg font-bold text-green-600">{formatPrice(orderTotal)}</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => setEditingRecord(null)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveRecord} loading={updateRecord.isPending || createVaccination.isPending || createDeworming.isPending || createServiceOrder.isPending}>
                <Plus className="h-4 w-4 mr-2" />
                {(() => {
                  const hasItems = orderItems.filter(i => i.should_charge).length > 0;
                  const hasVaccine = vaccinations.length > 0;
                  const hasDeworming = !!dewormingData.product_name;
                  const extras = [];
                  if (hasVaccine) extras.push(vaccinations.length > 1 ? `${vaccinations.length} Vacunas` : 'Vacuna');
                  if (hasDeworming) extras.push('Desparasitación');
                  if (hasItems) extras.push('Comanda');
                  return extras.length > 0
                    ? `Guardar Cambios + ${extras.join(', ')}`
                    : 'Guardar Cambios';
                })()}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Catálogo de Productos */}
      <Modal
        isOpen={showCatalogModal}
        onClose={() => {
          setShowCatalogModal(false);
          setCatalogSearch('');
          setCatalogCategory('');
        }}
        title="Catálogo de Productos"
        size="xl"
      >
        <div className="space-y-4">
          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                placeholder="Buscar por nombre o código..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              />
            </div>
            <select
              value={catalogCategory}
              onChange={(e) => setCatalogCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
            >
              <option value="">Todas las categorías</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Contador de productos */}
          <p className="text-sm text-gray-500">
            {catalogProducts.length} producto{catalogProducts.length !== 1 ? 's' : ''} encontrado{catalogProducts.length !== 1 ? 's' : ''}
          </p>

          {/* Grilla de productos */}
          <div className="max-h-[60vh] overflow-y-auto">
            {loadingAllProducts ? (
              <div className="text-center py-8 text-gray-500">Cargando productos...</div>
            ) : catalogProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No se encontraron productos</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {catalogProducts.map((product) => {
                  const isAlreadyAdded = orderItems.some(item => item.product_id === product.id);
                  return (
                    <button
                      key={product.id}
                      onClick={() => {
                        if (isAlreadyAdded) {
                          // Deseleccionar: quitar de orderItems
                          setOrderItems(orderItems.filter(item => item.product_id !== product.id));
                        } else {
                          handleAddProduct(product);
                        }
                      }}
                      className={clsx(
                        'p-3 rounded-xl border text-left transition-all cursor-pointer',
                        isAlreadyAdded
                          ? 'bg-green-50 border-green-300 hover:bg-red-50 hover:border-red-300'
                          : 'bg-white border-gray-200 hover:border-taupe-400 hover:shadow-md'
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <div className={clsx(
                          'p-1.5 rounded-lg flex-shrink-0',
                          product.is_service ? 'bg-purple-100' : 'bg-blue-100'
                        )}>
                          {product.is_service ? (
                            <Wrench className="h-4 w-4 text-purple-600" />
                          ) : (
                            <Package className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                        {isAlreadyAdded && (
                          <Check className="h-4 w-4 text-green-600 ml-auto flex-shrink-0" />
                        )}
                      </div>
                      <p className="font-medium text-sm text-gray-900 mt-2 line-clamp-2" title={product.name}>
                        {product.name}
                      </p>
                      {product.code && (
                        <p className="text-xs text-gray-400 font-mono">{product.code}</p>
                      )}
                      <p className="text-sm font-semibold text-taupe-600 mt-1">
                        {formatPrice(product.sale_price)}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Botón cerrar */}
          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-sm text-gray-500">
              {orderItems.length} item{orderItems.length !== 1 ? 's' : ''} en la comanda
            </p>
            <Button
              onClick={() => {
                setShowCatalogModal(false);
                setCatalogSearch('');
                setCatalogCategory('');
              }}
            >
              Listo
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}
